import { shallowEqual, useSelector } from "react-redux";
import { cleverTapCommonWords } from "../../analytics/clevertapConstants";
import { COUNTRIES } from "../../constants/common/common";
import { calDiscountPercentage, getProductDeliveryType } from "../../utils/common";

/**
 * Builds the CleverTap/CDP analytics payload shared by variant- and combo-selection tracking,
 * along with the raw state pieces callers need to decide when/what to dispatch or track.
 *
 * @returns {object}
 */
const useProductAnalyticsPayload = () => {
  const {
    fullProductDetails,
    selectedCurrency,
    catalogId,
    productId,
    pincode,
    checkLoginStatus,
    selectedDomesticLocation,
    selectedIntLocation,
    currentSelectedProductId,
  } = useSelector(
    (state) => ({
      fullProductDetails: state?.pdp?.fullProductDetails,
      selectedCurrency: state.currencyList?.userSelectedCurrency,
      catalogId: state.appConfig.catalogId,
      productId: state?.pdp?.fullProductDetails?.productId,
      pincode: state?.locationLock?.selectedDomesticLocation?.pincode,
      checkLoginStatus: state.fus.loginDetail?.loggedin,
      selectedDomesticLocation: state?.locationLock?.selectedDomesticLocation,
      selectedIntLocation: state?.locationLock?.selectedIntLocation,
      currentSelectedProductId: state?.pdp?.clientCurrentClickedItem?.productId,
    }),
    shallowEqual,
  );

  const discountPercentage = calDiscountPercentage(
    fullProductDetails?.productPrice?.listPrice,
    fullProductDetails?.productPrice?.price,
  );
  const productDeliveryType = getProductDeliveryType(fullProductDetails?.primaryCategoryId);

  const commonCleverTapData = {
    page_type: cleverTapCommonWords.PDP,
    page_name: fullProductDetails?.urlIdentifier,
    product_name: fullProductDetails?.productName,
    pid: fullProductDetails?.productId,
    product_category: fullProductDetails?.primaryCategoryId,
    is_variant_available: fullProductDetails?.isVariant === "Y",
    delivery_type: productDeliveryType,
    product_price: fullProductDetails?.productPrice?.price,
    currency: selectedCurrency?.currency?.id ?? "INR",
    strike_price: fullProductDetails?.productPrice?.listPrice,
    discount_percentage: discountPercentage,
    product_rating: fullProductDetails?.productReviewsAndRatings?.ratingValue,
    product_review_count: fullProductDetails?.productReviewsAndRatings?.totalReview,
    is_product_available: fullProductDetails?.stockAvailable,
    is_international: catalogId !== COUNTRIES.INDIA,
    city: catalogId !== COUNTRIES.INDIA ? selectedIntLocation?.location : "",
    area: catalogId !== COUNTRIES.INDIA ? "" : selectedDomesticLocation?.address,
    pincode: catalogId !== COUNTRIES.INDIA ? "" : pincode,
    system_pincode: catalogId !== COUNTRIES.INDIA ? "" : pincode,
    country: catalogId,
    sku_id: fullProductDetails?.skuCode,
    login_status: checkLoginStatus,
  };

  const eventDataForCdp = {
    page_type: commonCleverTapData?.page_type,
    page_name: commonCleverTapData?.page_name,
    products: [
      {
        city: commonCleverTapData?.city,
        area: commonCleverTapData?.area,
        pincode: commonCleverTapData?.pincode,
        systemPincode: commonCleverTapData?.system_pincode,
        country: catalogId,
        productCategory: commonCleverTapData?.product_category,
        productName: commonCleverTapData?.product_name,
        skuId: commonCleverTapData?.sku_id,
        productRating: commonCleverTapData?.product_rating,
        productReviewCount: commonCleverTapData?.product_review_count,
        isVariant_available: commonCleverTapData?.is_variant_available,
        isEgg: commonCleverTapData?.is_egg,
        deliveryType: commonCleverTapData?.delivery_type,
        productPrice: commonCleverTapData?.product_price,
        currency: commonCleverTapData?.currency,
        strikePrice: commonCleverTapData?.strike_price,
        discountPercentage: commonCleverTapData?.discount_percentage,
        isProductAvailable: commonCleverTapData?.is_product_available,
        loginStatus: checkLoginStatus,
        imageUrl: commonCleverTapData?.image_url,
      },
    ],
  };

  return {
    commonCleverTapData,
    eventDataForCdp,
    catalogId,
    productId,
    pincode,
    checkLoginStatus,
    selectedDomesticLocation,
    selectedIntLocation,
    currentSelectedProductId,
  };
};

export default useProductAnalyticsPayload;
